import IconButton from "@mui/material/IconButton";
import CollectionImportStatus from "../components/CollectionImportStatus";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import React from "react";
import Link from "../ui/RouterLink";
import {
  fetchJson,
  fetchSearch,
  API_BASE,
  PAGE_SIZE,
  SEARCH_PAGE_SIZE,
  fetchFormats,
  fetchGenres,
  fetchSubGenres,
  fetchLabels,
} from "../utils";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import FilterSection from "../components/FilterSection";
import CatalogSearch from "../components/CatalogSearch";
import SortDropdown from "../components/SortDropdown";
import useCatalogFilters from "../hooks/useCatalogFilters";
import useCatalogResults from "../hooks/useCatalogResults";
import TrashIcon from "../components/TrashIcon";
import AlbumQuantity from "../components/AlbumQuantity";
import { isCollectionImportActive } from "../utils/collectionImport";
const FILTER_DEFAULTS = {
  albumQuery: "",
  albumFormat: [],
  albumGenre: [],
  albumSubGenre: [],
  albumLabel: [],
  albumStyrene: "",
  albumSort: "",
};
function AlbumList({
  onSelect,
  onArtistSelect,
  start,
  refreshRevision = 0,
  showImportPrompt = false,
  onImport,
  importStatus,
  startingImport = false,
  onCancelImport,
  view = "album",
  onViewChange,
}) {
  const [filters, setFilter] = useCatalogFilters(FILTER_DEFAULTS);
  const query = filters.albumQuery;
  const setQuery = (value) => setFilter("albumQuery", value);
  const selectedFormats = filters.albumFormat;
  const setSelectedFormats = (value) => setFilter("albumFormat", value);
  const selectedGenres = filters.albumGenre;
  const setSelectedGenres = (value) => setFilter("albumGenre", value);
  const selectedSubGenres = filters.albumSubGenre;
  const setSelectedSubGenres = (value) => setFilter("albumSubGenre", value);
  const selectedLabels = filters.albumLabel;
  const setSelectedLabels = (value) => setFilter("albumLabel", value);
  const styreneFilter = filters.albumStyrene;
  const setStyreneFilter = (value) => setFilter("albumStyrene", value);
  const sort = filters.albumSort;
  const setSort = (value) => setFilter("albumSort", value);
  const [formatOptions, setFormatOptions] = React.useState([]);
  const [genreOptions, setGenreOptions] = React.useState([]);
  const [subGenreOptions, setSubGenreOptions] = React.useState([]);
  const [labelOptions, setLabelOptions] = React.useState([]);
  const [optionError, setOptionError] = React.useState(null);
  const [optionRevision, setOptionRevision] = React.useState(0);
  const loadMoreRef = React.useRef(null);
  const loadPage = React.useCallback(
    async (page, term, signal) => {
      const requestFilters = {
        format: selectedFormats,
        genre: selectedGenres,
        sub_genre: selectedSubGenres,
        label: selectedLabels,
        is_styrene: styreneFilter,
        sort: sort,
      };
      if (term) {
        const data = await fetchSearch({
          query: term,
          page,
          types: "album",
          filters: requestFilters,
          signal,
        });
        return {
          items: data.results || [],
          pageSize: SEARCH_PAGE_SIZE,
        };
      }
      const searchParams = new URLSearchParams({
        page,
        page_size: PAGE_SIZE,
      });
      Object.entries(requestFilters).forEach(([key, value]) => {
        if (Array.isArray(value))
          value.forEach((item) => searchParams.append(key, item));
        else if (value) searchParams.set(key, value);
      });
      const data = await fetchJson(
        `${API_BASE}/albums/?${searchParams.toString()}`,
        {
          credentials: "include",
          signal,
        },
      );
      return {
        items: data.albums,
        pageSize: PAGE_SIZE,
      };
    },
    [
      selectedFormats,
      selectedGenres,
      selectedSubGenres,
      selectedLabels,
      styreneFilter,
      sort,
    ],
  );
  const {
    items: albums,
    hasMore,
    loading,
    fetched,
    error,
    loadMore,
    retry,
  } = useCatalogResults({
    query,
    loadPage,
    refreshRevision,
    enabled: start || !!query.trim(),
  });
  const albumsToShow = albums;
  const importActive = isCollectionImportActive(importStatus);
  const canCancelImport = importStatus?.status !== "stopping";
  React.useEffect(() => {
    let active = true;
    setOptionError(null);
    fetchFormats()
      .then((options) => {
        if (active) setFormatOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchGenres()
      .then((options) => {
        if (active) setGenreOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchSubGenres()
      .then((options) => {
        if (active) setSubGenreOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchLabels()
      .then((options) => {
        if (active) setLabelOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    return () => {
      active = false;
    };
  }, [optionRevision]);
  React.useEffect(() => {
    if (!loadMoreRef.current || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      {
        rootMargin: "0px 0px 600px 0px",
      },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loadMore, albums.length]);

  return (
    <div>
      {optionError && (
        <Alert role="alert" severity="error">
          Some filters could not be loaded: {optionError.message}
          <Button
            className="btn"
            onClick={() => setOptionRevision((value) => value + 1)}
          >
            Retry filters
          </Button>
        </Alert>
      )}
      {error && (
        <Alert role="alert" severity="error">
          Could not load albums: {error.message}{" "}
          <Button type="button" className="btn" onClick={retry}>
            Retry
          </Button>
        </Alert>
      )}
      <form onSubmit={(e) => e.preventDefault()} className="search-bar">
        <CatalogSearch
          view={view}
          onViewChange={onViewChange}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </form>
      <FilterSection
        sortControl={
          <SortDropdown
            value={sort}
            onChange={setSort}
            options={[
              {
                value: "",
                label: "Default",
              },
              {
                value: "title",
                label: "Title A-Z",
              },
              {
                value: "-title",
                label: "Title Z-A",
              },
            ]}
          />
        }
      >
        <div className="filter-row">
          <MultiSelectDropdown
            label="Format"
            options={formatOptions}
            selected={selectedFormats}
            onChange={setSelectedFormats}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Genre"
            searchable
            options={genreOptions}
            selected={selectedGenres}
            onChange={setSelectedGenres}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Sub-Genre"
            searchable
            options={subGenreOptions}
            selected={selectedSubGenres}
            onChange={setSelectedSubGenres}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Label"
            searchable
            options={labelOptions}
            selected={selectedLabels}
            onChange={setSelectedLabels}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            options={[
              {
                value: "true",
                label: "Only Styrene",
              },
              {
                value: "false",
                label: "Not Styrene",
              },
            ]}
            selected={styreneFilter ? [styreneFilter] : []}
            onChange={(vals) => setStyreneFilter(vals[vals.length - 1] || "")}
            className="filter-dropdown"
            allLabel="Styrene"
            showLabel={false}
            showSelected
          />
        </div>
      </FilterSection>
      {(selectedFormats.length > 0 ||
        selectedGenres.length > 0 ||
        selectedSubGenres.length > 0 ||
        selectedLabels.length > 0 ||
        styreneFilter) && (
        <div className="applied-filters">
          {selectedFormats.map((f) => (
            <Button
              key={`f-${f}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove format ${f}`}
              onClick={() =>
                setSelectedFormats((prev) => prev.filter((x) => x !== f))
              }
            >
              {f}
              <CloseIcon />
            </Button>
          ))}
          {selectedGenres.map((g) => (
            <Button
              key={`g-${g}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove genre ${g}`}
              onClick={() =>
                setSelectedGenres((prev) => prev.filter((x) => x !== g))
              }
            >
              {g}
              <CloseIcon />
            </Button>
          ))}
          {selectedSubGenres.map((g) => (
            <Button
              key={`sg-${g}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove sub-genre ${g}`}
              onClick={() =>
                setSelectedSubGenres((prev) => prev.filter((x) => x !== g))
              }
            >
              {g}
              <CloseIcon />
            </Button>
          ))}
          {selectedLabels.map((l) => (
            <Button
              key={`l-${l}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove label ${l}`}
              onClick={() =>
                setSelectedLabels((prev) => prev.filter((x) => x !== l))
              }
            >
              {l}
              <CloseIcon />
            </Button>
          ))}
          {styreneFilter && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove styrene filter"
              onClick={() => setStyreneFilter("")}
            >
              {styreneFilter === "true" ? "Only Styrene" : "Not Styrene"}
              <CloseIcon />
            </Button>
          )}
          <IconButton
            type="button"
            className="filter-tag clear-filters"
            aria-label="Clear all filters"
            onClick={() => {
              setSelectedFormats([]);
              setSelectedGenres([]);
              setSelectedSubGenres([]);
              setSelectedLabels([]);
              setStyreneFilter("");
              setSort("");
            }}
          >
            <TrashIcon />
          </IconButton>
        </div>
      )}
      {!start ? (
        <div className="no-albums">
          <p>No albums imported.</p>
          {showImportPrompt && onImport && (
            <>
              {startingImport || importActive ? (
                <CollectionImportStatus
                  status={importStatus}
                  starting={startingImport}
                />
              ) : (
                <Button className="btn import-btn" onClick={onImport}>
                  Import collection
                </Button>
              )}
              {importActive && !startingImport && (
                <Button
                  className="btn stop mobile-stop-btn"
                  onClick={() => onCancelImport && onCancelImport()}
                  disabled={!canCancelImport}
                >
                  Stop import
                </Button>
              )}
            </>
          )}
        </div>
      ) : albumsToShow.length === 0 && !loading && fetched && !error ? (
        <p>No albums found.</p>
      ) : (
        <ul className="album-grid">
          {albumsToShow.map((album) => {
            return (
              <li
                key={album.id}
                className="album-tile compact-catalog-card collection-album-card"
                onClick={() => onSelect(album.id)}
              >
                <div className="album-thumb">
                  <img
                    src={`${API_BASE}/albums/${album.id}/image/`}
                    alt={`${album.title} cover`}
                    loading="lazy"
                  />
                </div>
                <div className="album-info">
                  <div className="album-title">
                    <Button
                      type="button"
                      className="catalog-title-button"
                      variant="text"
                      disableRipple
                      title={album.title}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect && onSelect(album.id);
                      }}
                    >
                      <span>{album.title}</span>
                    </Button>
                  </div>
                  <div className="album-artist">
                    {album.artists
                      ? album.artists.map((a, i) => (
                          <Link
                            key={a.id}
                            to={`/artist/${a.id}`}
                            className="artist-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onArtistSelect) {
                                e.preventDefault();
                                onArtistSelect(a.id);
                              }
                            }}
                          >
                            {a.name}
                            {i < album.artists.length - 1 ? ", " : ""}
                          </Link>
                        ))
                      : album.artist}
                  </div>
                  {album.quantity > 1 && (
                    <AlbumQuantity quantity={album.quantity} />
                  )}
                  {album.is_styrene && (
                    <div className="track-subtitle">Styrene Pressing</div>
                  )}
                </div>
              </li>
            );
          })}
          {hasMore && albumsToShow.length > 0 && (
            <li
              ref={loadMoreRef}
              style={{
                listStyle: "none",
              }}
            />
          )}
        </ul>
      )}
      {loading && <p role="status">Loading albums…</p>}
      {hasMore && (
        <Button
          className="btn"
          disabled={loading || !!error}
          onClick={loadMore}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
export default AlbumList;
